import { securityAuditLogger } from './securityAuditLogger';

interface TokenData {
  token: string;
  refreshToken?: string;
  expiresAt: number;
  issuedAt: number;
}

class TokenManager {
  private readonly TOKEN_KEY = 'jwt_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';
  private readonly TOKEN_ISSUED_KEY = 'token_issued';
  
  // Session timeout in milliseconds (30 minutes of inactivity)
  private readonly SESSION_TIMEOUT = 30 * 60 * 1000;
  private readonly TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh 5 minutes before expiry
  
  private sessionTimeoutId: NodeJS.Timeout | null = null;
  private lastActivityTime: number = Date.now();

  constructor() {
    this.initializeSessionTimeout();
    this.setupActivityListeners();
  }

  /**
   * Store tokens securely with expiration tracking
   */
  setTokens(token: string, refreshToken?: string, expiresIn?: number): void {
    const now = Date.now();
    const expiresAt = expiresIn ? now + (expiresIn * 1000) : now + (24 * 60 * 60 * 1000); // Default 24h
    
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiresAt.toString());
    localStorage.setItem(this.TOKEN_ISSUED_KEY, now.toString());
    
    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
    
    this.updateLastActivity();
    this.resetSessionTimeout();
    
    // Log token storage event
    securityAuditLogger.logSecurityEvent('token_stored', {
      timestamp: new Date().toISOString(),
      expiresAt: new Date(expiresAt).toISOString(),
      hasRefreshToken: !!refreshToken
    });
  }

  /**
   * Get current token if valid, otherwise attempt refresh
   */
  async getValidToken(): Promise<string | null> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) {
      return null;
    }

    // Check if token is expired or needs refresh
    if (this.isTokenExpired() || this.shouldRefreshToken()) {
      try {
        const newToken = await this.refreshToken();
        return newToken;
      } catch (error) {
        // If refresh fails, clear tokens
        this.clearTokens();
        return null;
      }
    }

    this.updateLastActivity();
    return token;
  }

  /**
   * Check if current token is expired
   */
  isTokenExpired(): boolean {
    const expiryStr = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiryStr) {
      return true;
    }

    const expiry = parseInt(expiryStr);
    return Date.now() >= expiry;
  }

  /**
   * Check if token should be refreshed (within threshold of expiry)
   */
  shouldRefreshToken(): boolean {
    const expiryStr = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiryStr) {
      return true;
    }

    const expiry = parseInt(expiryStr);
    return Date.now() >= (expiry - this.TOKEN_REFRESH_THRESHOLD);
  }

  /**
   * Refresh the current token
   * Note: Backend doesn't support token refresh, so this will always fail
   */
  async refreshToken(): Promise<string> {
    // Log refresh attempt
    securityAuditLogger.logSecurityEvent('token_refresh_error', {
      timestamp: new Date().toISOString(),
      error: 'Token refresh not supported by backend'
    });
    
    // Clear tokens since refresh is not supported
    this.clearTokens();
    
    // Throw error to trigger re-login
    throw new Error('Token refresh not supported - please login again');
  }

  /**
   * Clear all tokens and session data
   */
  clearTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.TOKEN_ISSUED_KEY);
    
    this.clearSessionTimeout();
    
    // Log token clearance
    securityAuditLogger.logSecurityEvent('tokens_cleared', {
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get token expiration info
   */
  getTokenInfo(): TokenData | null {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const expiryStr = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    const issuedStr = localStorage.getItem(this.TOKEN_ISSUED_KEY);
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

    if (!token || !expiryStr || !issuedStr) {
      return null;
    }

    return {
      token,
      refreshToken: refreshToken || undefined,
      expiresAt: parseInt(expiryStr),
      issuedAt: parseInt(issuedStr)
    };
  }

  /**
   * Check if session has timed out due to inactivity
   */
  isSessionTimedOut(): boolean {
    return Date.now() - this.lastActivityTime > this.SESSION_TIMEOUT;
  }

  /**
   * Update last activity time and reset session timeout
   */
  updateLastActivity(): void {
    this.lastActivityTime = Date.now();
    this.resetSessionTimeout();
  }

  /**
   * Initialize session timeout monitoring
   */
  private initializeSessionTimeout(): void {
    // Check if there's an existing session
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (token && !this.isTokenExpired()) {
      this.resetSessionTimeout();
    }
  }

  /**
   * Set up activity listeners to track user interaction
   */
  private setupActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const activityHandler = () => {
      this.updateLastActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, activityHandler, true);
    });
  }

  /**
   * Reset the session timeout
   */
  private resetSessionTimeout(): void {
    this.clearSessionTimeout();
    
    this.sessionTimeoutId = setTimeout(() => {
      // Log session timeout
      securityAuditLogger.logSecurityEvent('session_timeout', {
        timestamp: new Date().toISOString(),
        lastActivity: new Date(this.lastActivityTime).toISOString()
      });
      
      // Clear tokens and redirect to login
      this.clearTokens();
      
      // Show timeout message and redirect
      alert('Your session has expired due to inactivity. Please log in again.');
      window.location.href = '/login';
    }, this.SESSION_TIMEOUT);
  }

  /**
   * Clear the session timeout
   */
  private clearSessionTimeout(): void {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
  }

  /**
   * Manually trigger session timeout (for testing or forced logout)
   */
  forceSessionTimeout(): void {
    this.clearTokens();
    window.location.href = '/login';
  }
}

export const tokenManager = new TokenManager();